// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Data loading.
//
// CACHE DISCIPLINE, learnt the hard way on a sibling site: the JS bundle is
// content-hashed by Vite but the data files are not, so a returning visitor
// inside GitHub Pages' ~10-minute cache window can run today's bundle against
// yesterday's numbers. meta.json is therefore ALWAYS revalidated, and every
// other data URL carries the as-at date as a query string so a data refresh
// changes the URL and busts the cache by construction.

import type { Core, Cube, IndustryData, Meta, Spread } from './types';

let metaCache: Meta | null = null;
let coreCache: Core | null = null;
let cubeCache: Cube | null = null;
let industryCache: IndustryData | null = null;
let spreadCache: Spread | null = null;

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`Could not load ${url} (HTTP ${res.status})`);
  return (await res.json()) as T;
}

export async function loadMeta(): Promise<Meta> {
  if (metaCache) return metaCache;
  // `no-cache` revalidates rather than skipping the cache — a conditional
  // request that usually comes back 304, so it costs almost nothing.
  metaCache = await getJson<Meta>('data/meta.json', { cache: 'no-cache' });
  return metaCache;
}

export async function loadCore(): Promise<Core> {
  if (coreCache) return coreCache;
  const meta = await loadMeta();
  coreCache = await getJson<Core>(`data/core.json?v=${meta.asAt}`);
  return coreCache;
}

/** The full 132-variable cube — ~1.2 MB, so only fetched when Explore opens. */
export async function loadCube(): Promise<Cube> {
  if (cubeCache) return cubeCache;
  const meta = await loadMeta();
  cubeCache = await getJson<Cube>(`data/cube.json?v=${meta.asAt}`);
  return cubeCache;
}

export async function loadIndustry(): Promise<IndustryData> {
  if (industryCache) return industryCache;
  const meta = await loadMeta();
  industryCache = await getJson<IndustryData>(`data/industry.json?v=${meta.asAt}`);
  return industryCache;
}

export async function loadSpread(): Promise<Spread> {
  if (spreadCache) return spreadCache;
  const meta = await loadMeta();
  spreadCache = await getJson<Spread>(`data/spread.json?v=${meta.asAt}`);
  return spreadCache;
}

export async function loadRegions(): Promise<GeoJSON.FeatureCollection> {
  const meta = await loadMeta();
  return getJson<GeoJSON.FeatureCollection>(`data/regions.geojson?v=${meta.asAt}`);
}

/** Synchronous accessors for code that runs after boot has awaited the loads. */
export function getMeta(): Meta {
  if (!metaCache) throw new Error('meta not loaded');
  return metaCache;
}

export function getCore(): Core {
  if (!coreCache) throw new Error('core data not loaded');
  return coreCache;
}
